import React, { useMemo, useState } from "react";
import { Box, CircularProgress, ListItemButton, List, Typography, Stack, IconButton, Dialog, Button, Chip, Divider, Badge, Tooltip } from "@mui/material";
import { ResponsiveContainer, ComposedChart, CartesianGrid, XAxis, YAxis, Area, Line } from "recharts";
import { useForecast } from "../../hooks/useForecast";
import { WeeklyForecastComponentType } from "@domain/forecast/enums/WeeklyForecastComponentType.enum";
import { APP_CONFIG } from "../../../../config/app.config";
import { Rank } from "@domain/insights/models/Rank";
import { useRanks } from "../../hooks/useRanks";
import { useNavigate } from "react-router-dom";
import { Icon } from "@shared/icons";
import { useProduct } from "@features/products";
import { SeasonalityMode } from "@domain/forecast/models/ForecastParameters";
import { MoreVert } from "@mui/icons-material";
import { ArrowRightIcon } from "@mui/x-date-pickers";
import { InfluenceLabel } from "../ForecastAnalytics/InfluenceLabel";
import { ForecastSettingsMenu } from "../ForecastSettingsMenu";
import { ComponentGraph } from "../Graph/ComponentGraph";
import RankSpotlight from "../SearchTerms/RankSpotlight";
import { ForecastSourceType } from "@domain/forecast/models/Forecast";
import { useProductUpdate } from "@features/products/hooks/useProductUpdate";

interface TimeTravelSearchTermComponentProps {
  productId: string;     // e.g. search term value
  connector: string;  // e.g. connector type
  canModifyParameters?: boolean;
}

// Simple week -> date helper for display only (approximate ISO week to date)
function weekYearToDateString(year: number, week: number): string {
  const date = new Date(year, 0, 1);
  const day = date.getDay();
  const diff = (week - 1) * 7 + (day <= 4 ? 1 - day : 8 - day);
  date.setDate(date.getDate() + diff);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

const TimeTravelSearchTermComponent: React.FC<TimeTravelSearchTermComponentProps> = ({
  productId,
  connector,
  canModifyParameters = false,
}) => {
  const { forecast, isLoading } = useForecast({ sourceType: ForecastSourceType.PRODUCT, source: productId, connector });
  const navigate = useNavigate();
  const { ranks } = useRanks(productId);

  const [showSearchTermSelect, setShowSearchTermSelect] = useState(false);
  const [showSearchTermEdit, setShowSearchTermEdit] = useState(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { product } = useProduct(productId);
  const { updateProduct } = useProductUpdate();

  const handleSetSearchRegressor = async (searchTerm: string) => {
    await updateProduct({
      productId: productId,
      updateData: {
        forecastParameters: {
          ...product?.forecastParameters ?? {},
          search_term_regressor: searchTerm
        }
      }
    });
    setShowSearchTermEdit(false);
  };



  const getAverageImpressions = (rank: Rank) => {
    const impressionsArr = rank.dailyProductRanks?.map(dailyProductRank => dailyProductRank.impressions ?? 0) ?? [];
    if (impressionsArr.length === 0) return 0;
    const sum = impressionsArr.reduce((acc, v) => acc + v, 0);
    return sum / impressionsArr.length;
  };


  const chartData = useMemo(() => {
    if (!forecast?.weeklyForecastComponents) return [];

    // Filter by the specified component type
    const components = forecast.weeklyForecastComponents
      .filter(comp => comp.type === WeeklyForecastComponentType.SEARCH);

    if (components.length === 0) return [];

    // Sort chronologically
    const sorted = components
      .slice()
      .sort((a, b) => (a.year === b.year ? a.week - b.week : a.year - b.year));

    // Calculate moving average
    const windowSize = 10; // e.g. 3-week moving average
    const movAvg = (arr: number[], idx: number, size: number) => {
      const start = Math.max(0, idx - Math.floor((size - 1) / 2));
      const end = Math.min(arr.length, start + size);
      const actualSize = end - start;
      const sum = arr.slice(start, end).reduce((acc, val) => acc + val, 0);
      return actualSize > 0 ? sum / actualSize : 0;
    };

    // Extract forecast quantities
    const forecastQuantities = sorted.map(w => w.forecastQuantity ?? 0);

    return sorted.map((w, i) => ({
      date: weekYearToDateString(w.year, w.week),
      year: w.year,
      week: w.week,
      forecast: movAvg(forecastQuantities, i, windowSize),
      forecastMin: movAvg(forecastQuantities, i, windowSize) - 1,
      forecastMax: movAvg(forecastQuantities, i, windowSize) + 1,
      forecastBands: [movAvg(forecastQuantities, i, windowSize) - 1, movAvg(forecastQuantities, i, windowSize) + 1],
    }));
  }, [forecast]);

  const getSliderDefaultValue = () => {
    // Map prior scale (logarithmic from 0.001 to 10) to slider 0-100
    if (forecast?.parameters.search_term_regressor_prior_scale) {
      // Clamp prior scale into [0.001, 10]
      return forecast.parameters.search_term_regressor_prior_scale * 10;
    }

    return 10;
  };

  const handleSelectModeChange = (mode: SeasonalityMode) => {
    updateProduct({
      productId: productId,
      updateData: {
        forecastParameters: {
          ...product?.forecastParameters ?? {},
          search_term_regressor_mode: mode
        }
      }
    });
  };

  const handleSliderChange = (influence: number) => {
    updateProduct({
      productId: productId,
      updateData: {
        forecastParameters: {
          ...product?.forecastParameters ?? {},
          search_term_regressor_prior_scale: influence / 10
        }
      }
    });
  }

  const handleSelectSearchTerm = (searchTerm: string) => {
    const rank = ranks?.find(rank => rank.searchTerm === searchTerm);

    if (rank) {
      navigate(`/timetravel/ranks/${rank.id}`);
    } else {
      navigate(`/timetravel/search/${searchTerm}`);
    }

    setShowSearchTermSelect(false);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        border: `1px solid ${APP_CONFIG.ui.theme.colors.border}`,
        borderRadius: 2,
        padding: 2,
        flex: 1,
      }}>


      <Stack direction={{ xs: 'column', md: 'row' }} spacing={4}>
        <Stack flex={2}>

          <Stack direction="row" spacing={0.5} justifyContent="space-between" alignItems="center" height={30}>
            <Typography variant="subtitle2" color="text.secondary">
              Search Term
            </Typography>

            <IconButton size="small"
              onClick={(event) => setAnchorEl(event.currentTarget)}
            ><MoreVert /></IconButton>


            <ForecastSettingsMenu
              title="Search Term Settings"
              onModeChange={handleSelectModeChange} onInfluenceChange={handleSliderChange} defaultMode={forecast?.parameters.search_term_regressor_mode || forecast?.parameters.seasonality_mode || SeasonalityMode.ADDITIVE} defaultInfluence={getSliderDefaultValue()} anchorEl={anchorEl} onClose={() => setAnchorEl(null)} />
          </Stack>

          <Stack direction="row" spacing={1} justifyContent="flex-start" alignItems="center">
            <Button

              variant="text"
              sx={{ p: 0, minWidth: 0 }}
              onClick={() => handleSelectSearchTerm(product?.forecastParameters?.search_term_regressor || forecast?.parameters.search_term_regressor || '')}
            >

              <Typography variant="h6" color="text.secondary" textAlign="left">
                {product?.forecastParameters?.search_term_regressor || forecast?.parameters.search_term_regressor || 'No search term'}
              </Typography>
            </Button>

            {product?.forecastParameters?.search_term_regressor && product?.forecastParameters?.search_term_regressor !== forecast?.parameters.search_term_regressor && (
              <Tooltip title={`Original search term: ${forecast?.parameters.search_term_regressor || 'No search term'}`}>
                <Chip
                  label="Modified"
                  size="small"
                  color="primary"
                />
              </Tooltip>
            )}

            {canModifyParameters && (
              <IconButton size="small"
                onClick={() => setShowSearchTermEdit(true)}
              ><Icon icon="IcEdit" /></IconButton>
            )}

          </Stack>

          <InfluenceLabel influence={forecast?.forecastAnalytics?.search_term_influence} />

          {isLoading && (
            <Box display="flex" justifyContent="center" alignItems="center" height={300}>
              <CircularProgress />
            </Box>
          )}

          <ComponentGraph chartData={chartData} />
        </Stack>


        {ranks && ranks.length > 0 && (
          <>
            <Divider orientation="vertical" flexItem />

            <Stack flex={1} justifyContent="flex-start" alignItems="flex-start">
              <Stack direction="row" spacing={0.5} justifyContent="space-between" alignItems="center" height={30}>
                <Typography variant="subtitle2" color="text.secondary">
                  Top 3 Ranks
                </Typography>
              </Stack>


              <List sx={{ width: '100%' }}>
                {ranks?.sort((a, b) => getAverageImpressions(b) - getAverageImpressions(a)).slice(0, 3).map((rank: Rank, index: number) => (
                  <ListItemButton
                    key={rank.id}
                    onClick={() => navigate(`/timetravel/ranks/${rank.id}`)}
                    sx={{ px: 0.5 }}
                  // selected={rank.id === selectedRankId}
                  >
                    <Stack flex={1} direction="row" spacing={2} alignItems="center">
                      <Chip
                        label={index + 1}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ width: 30, height: 30 }}
                      />

                      <Stack direction="column" spacing={0}>
                        <Typography variant="subtitle2"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                          }}
                        > {rank.searchTerm} </Typography>
                        <Typography variant="caption" color="text.secondary"> {rank.wasSponsored ? 'Sponsored' : 'Organic'} </Typography>
                      </Stack>

                    </Stack>
                  </ListItemButton>
                ))}


              </List>


              <Button
                endIcon={<Icon icon="ChevronRight" />}
                onClick={() => setShowSearchTermSelect(true)}
                size="small"
                variant="text"
                color="primary"
              >
                Show all
              </Button>

            </Stack>
          </>
        )}
      </Stack >


      <Dialog open={showSearchTermSelect} onClose={() => setShowSearchTermSelect(false)} maxWidth="xs" fullWidth>
        <RankSpotlight
          ranks={ranks ?? []}
          onSelect={handleSelectSearchTerm}
        />
      </Dialog>

      <Dialog open={showSearchTermEdit} onClose={() => setShowSearchTermEdit(false)} maxWidth="xs" fullWidth>
        <RankSpotlight
          ranks={ranks ?? []}
          onSelect={handleSetSearchRegressor}
        />
      </Dialog>

    </Box >
  );
};

export default TimeTravelSearchTermComponent;

